import { FloatingPortal } from "@floating-ui/react"
import { forwardRef, useRef, useState } from "react"
import { noop } from "lodash-es"
import { Icon } from "../../Icon"
import { Checkbox } from "../../Checkbox"
import { FormLabel } from "../../FormLabel"
import { Popover } from "../../Popover"
import { TableRowSelectMenuProps } from "./types"
import { TableRowSelectOptionList } from "./TableRowSelectOptionList"
import { useTableContext } from "../TableContext"
import { FocusManager } from "../../FocusManager"
import { mergeRefs } from "../../utils/mergeRefs"

export const TableRowSelectMenu = forwardRef<HTMLButtonElement, TableRowSelectMenuProps>(
  ({ ariaLabel = "Selection menu", onSelectOption, ...rest }, ref) => {
    const buttonTriggerRef = useRef<HTMLButtonElement>(null)
    const [isOpen, setIsOpen] = useState(false)
    const onRequestClose = () => setIsOpen(false)
    const { selectAllCheckboxState, multiRowSelectOptionsConfig, isLoading } = useTableContext()
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen} placement="bottom-start">
        <Popover.Trigger>
          <button
            disabled={isLoading}
            {...rest}
            className="flex items-center gap-2 outline-black focus-visible:outline"
            type="button"
            aria-label={ariaLabel}
            onClick={() => setIsOpen(true)}
            ref={mergeRefs(ref, buttonTriggerRef)}
          >
            <Checkbox
              // We are purely using the checkbox as a decorating element here. So hide its semantics from the accessibility tree
              aria-hidden
              disabled={isLoading || rest.disabled}
              checked={selectAllCheckboxState === "CHECKED"}
              onChange={noop} // Suppress warnings
              className="pointer-events-none"
              isIndeterminate={selectAllCheckboxState === "INDETERMINATE"}
              label={<FormLabel label={ariaLabel} className="sr-only" />}
              tabIndex={-1}
              value="all" // TODO: remove when the <label for/> in Checkbox has been removed
            />
            <Icon size="xs" name="ChevronDown" aria-hidden />
          </button>
        </Popover.Trigger>
        <FloatingPortal>
          <FocusManager
            isOpen={isOpen}
            onRequestOpenChange={setIsOpen}
            returnFocusRef={buttonTriggerRef}
            render={({ getRenderProps }) => {
              return (
                <Popover.Content
                  {...getRenderProps()}
                  className="rounded-md border border-gray-200 shadow-md"
                >
                  <TableRowSelectOptionList
                    onRequestClose={onRequestClose}
                    multiRowSelectOptionsConfig={multiRowSelectOptionsConfig}
                    onSelect={onSelectOption}
                  />
                </Popover.Content>
              )
            }}
          />
        </FloatingPortal>
      </Popover>
    )
  },
)
TableRowSelectMenu.displayName = "Table.RowSelectMenu"
